import { formatRoomComboTitle } from "../../common/AllTickets/Hotel/hotelhelper";

export { formatRoomComboTitle };

const isMealOnlyLabel = (value) => {
  const t = String(value ?? "")
    .trim()
    .toLowerCase();
  if (!t) return true;
  return /^(room\s*only|breakfast|lunch|dinner|half\s*board|full\s*board|all\s*inclusive|meal\s*only)(\s|$)/i.test(
    t,
  );
};

/** Details-only: collapse commas/spaces and Title Case so room cards read consistently. */
export function normalizeHotelRoomDisplayName(value) {
  const trimmed = String(value ?? "")
    .replace(/,/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!trimmed) return "";
  return trimmed
    .toLowerCase()
    .replace(/\b([a-z])/g, (ch) => ch.toUpperCase());
}

/**
 * Same title rules as Hotel Detail "Book Now" cards:
 * prefer Tripjack `rc` (short name) over long commercial `roomName`.
 */
export function getHotelRoomDisplayTitle(room = {}, hotelName = "") {
  const hotel = String(hotelName ?? "").trim().toLowerCase();
  const candidates = [
    room?.rc,
    room?.Rc,
    room?.displayTitle,
    room?.DisplayTitle,
    room?.selectedRoomName,
    room?.roomName,
    room?.RoomName,
  ]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);

  for (const candidate of candidates) {
    if (isMealOnlyLabel(candidate)) continue;
    if (hotel && candidate.toLowerCase() === hotel) continue;
    const titled = formatRoomComboTitle(candidate);
    const match = String(titled).match(/^(\d+)\s*[x×X]\s+(.+)$/);
    if (match) {
      const qty = Math.max(1, Number(match[1]) || 1);
      const name = normalizeHotelRoomDisplayName(match[2]);
      return qty > 1 ? `${qty} x ${name}` : name;
    }
    return normalizeHotelRoomDisplayName(titled) || titled;
  }
  return "Room";
}

/**
 * Stamp Details selection identity onto hotelprice rooms so Checkout/Preview
 * show the same room title the agent clicked (rc / roomName from roomPlan).
 */
export function applySelectedRoomIdentityToHotels(hotels, roomPlan = []) {
  if (!hotels || typeof hotels !== "object") return hotels;
  if (!Array.isArray(roomPlan) || roomPlan.length === 0) return hotels;

  const roomsKey = Array.isArray(hotels.rooms)
    ? "rooms"
    : Array.isArray(hotels.Rooms)
      ? "Rooms"
      : null;
  if (!roomsKey) return hotels;

  const nextRooms = hotels[roomsKey].map((room, index) => {
    const plan = roomPlan[index] ?? roomPlan[0] ?? {};
    const selectedTitle = String(
      plan?.displayTitle ??
        plan?.DisplayTitle ??
        plan?.rc ??
        plan?.Rc ??
        plan?.roomName ??
        plan?.RoomName ??
        "",
    ).trim();
    const selectedRc = String(plan?.rc ?? plan?.Rc ?? selectedTitle).trim();
    const selectedRoomName = String(
      plan?.roomName ?? plan?.RoomName ?? selectedTitle,
    ).trim();
    if (!selectedTitle && !selectedRc && !selectedRoomName) {
      return room;
    }
    return {
      ...room,
      ...(selectedRc ? { rc: selectedRc, Rc: selectedRc } : {}),
      ...(selectedRoomName
        ? { roomName: selectedRoomName, RoomName: selectedRoomName }
        : selectedTitle
          ? { roomName: selectedTitle, RoomName: selectedTitle }
          : {}),
      ...(selectedTitle
        ? { displayTitle: selectedTitle, selectedRoomName: selectedTitle }
        : {}),
    };
  });

  return { ...hotels, [roomsKey]: nextRooms };
}
